import { inject, Injectable, LOCALE_ID } from '@angular/core';
import { MbscResource } from '@mobiscroll/angular';
import { ApiDataAccess, ApiResourceLocation } from '@softline/core';
import {
  SOFTLINE_API_PMS_ROSTER_EMPLOYEE_BALANCE,
  SOFTLINE_API_PMS_ROSTER_EMPLOYEE_WORKSCHEMA,
  SOFTLINE_API_PMS_ROSTER_STRUCTURE,
} from '@softapps/pms/roster-core';
import { Balance } from '@softapps/pms/roster-core';
import { DetailEmployee } from '@softapps/pms/roster-core';
import { Structure } from '@softapps/pms/roster-core';
import { Workschema } from '@softapps/pms/roster-core';
import { formatRequestDate } from '@softapps/pms/roster-core';
import {
  LoadRosterDetailParams,
  LoadRosterResourceParams,
  RosterResourceRepository,
} from '@softapps/pms/roster-core';

@Injectable()
export class PmsRosterResourceRepository extends RosterResourceRepository<
  MbscResource,
  DetailEmployee
> {
  readonly #dataAccess = inject(ApiDataAccess);
  readonly #locale = inject(LOCALE_ID);

  async load(params: LoadRosterResourceParams): Promise<MbscResource[]> {
    const location: ApiResourceLocation = {
      path: SOFTLINE_API_PMS_ROSTER_STRUCTURE,
      pathParams: { employeeId: params.employeeId },
      queryParams: {
        begindate: params.begindate,
        enddate: params.enddate,
      },
    };

    const result = await this.#dataAccess.get<Structure>(location);
    return this.convertStructureToResources(result);
  }

  async loadDetail(params: LoadRosterDetailParams): Promise<DetailEmployee> {
    return {
      balance: await this.loadEmployeeBalance(params.date, params.employeeId),
      workschema: await this.loadEmployeeWorkschema(params.date, params.employeeId),
    };
  }

  private async loadEmployeeBalance(
    cutoffdate: string,
    employeeId: number
  ): Promise<Balance> {
    const location: ApiResourceLocation = {
      path: SOFTLINE_API_PMS_ROSTER_EMPLOYEE_BALANCE,
      pathParams: { employeeId },
      queryParams: {
        cutoffdate: formatRequestDate(cutoffdate, this.#locale),
      },
    };

    return await this.#dataAccess.get<Balance>(location);
  }

  private async loadEmployeeWorkschema(
    cutoffdate: string,
    employeeId: number
  ): Promise<Workschema> {
    const location: ApiResourceLocation = {
      path: SOFTLINE_API_PMS_ROSTER_EMPLOYEE_WORKSCHEMA,
      pathParams: { employeeId },
      queryParams: {
        cutoffdate: formatRequestDate(cutoffdate, this.#locale),
      },
    };

    return await this.#dataAccess.get<Workschema>(location);
  }

  private convertStructureToResources(structure: Structure): MbscResource[] {
    return structure.departements.map((departement) => {
      return {
        id: departement.department.id,
        name: departement.department.name,
        key: departement.department.key,
        children: departement.employees.map((employee) => {
          return {
            id: employee.id,
            name: employee.name,
            key: employee.key,
          };
        }),
      };
    });
  }
}
